import * as Raven from 'raven-js';
import { AppFileApiHelper } from './AppFileApiHelper';
import { Converter } from './Converter';
import { GlobalHelper } from './GlobalHelper';
import { SceneActions } from '../actions/SceneActions';
import { SaveType } from '../constants/Enums';
import { AppDocumentContainer } from './AppDocumentContainer';
import { SessionActions } from '../actions/SessionActions';
import { ProjectManagerActions, ProjectAttributes, ProjectProperties } from '../actions/ProjectManagerActions';
import { ProjectInstance, ProjectLoadMethod } from './ProjectInstance';
import { ScreenshotHelper } from './ScreenshotHelper';
import { ViewSettingsStore } from '../stores/ViewSettingsStore';
import { LayoutType } from '../constants/LayoutConstants';
import { FloorPlanManager } from './FloorPlanManager';
import { UIHelper } from './UIHelper';
import { PersistentStore } from '../stores/PersistentStore';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';

// Submodules
import * as AppFileApiWrapper from '../submodules/app-api/Filesystem/Wrapper';
import { sendMessage } from '../submodules/app-api/Communication';

import core = egr.wcf.core;
import utils = egr.wcf.utils;
import { rejectValue } from '../submodules/utils/Promise';

// May not be changed after the first release!
const ProjectApiVersionInformationPath: string = '/meta/api_version_projects.txt';
const ProjectRootFolder: string = '/projects/';
const TempProjectRootFolder: string = '/tmp/projects/';

interface ApiVNullProjectData { // old project api -> v0
    id: number;
    name?: string;
    mtime?: number;
    crtime?: number;
    image?: string;
    content?: string;
    attributes?: string;
}

const enum ProjectManagerApiVersion {
    // The values of the existing entries must not be changed!!!
    PROJECT_API_BASED = 0,
    FS_API_BASED = 1
}

interface ProjectSaveOptions {
    updateTime: boolean;
    metaDataOnly: boolean;
}

interface ProjectLoadOptions {
    loadFromAutoSave: boolean;
    markAsNew: boolean;
}

export abstract class AppProjectManager {

    public static addTempResource(project: ProjectInstance, path: string, data: ArrayBuffer | string): Promise<void> {
        return AppFileApiHelper.writeFile(
            AppProjectManager.getProjectRootPath(project.id, true) + path,
            data
        );
    }

    public static async getProjectIds(): Promise<Array<string>> {
        try {
            return await AppFileApiHelper.getSubDirectories(ProjectRootFolder, false);
        } catch (_) {
            // FixMe: introduce some kind of error code and check if the error reasone is something
            // like `directory not found`
            return [];
        }
    }

    public static async getProjects(): Promise<Array<ProjectInstance>> {
        const projectIds: Array<string> = await AppProjectManager.getProjectIds();

        const projects: Array<ProjectInstance | null> = await Promise.all<ProjectInstance | null>(
            projectIds.map((projectId: string): Promise<ProjectInstance | null> => {
                // FixMe: log errors
                return rejectValue<ProjectInstance | null>(
                    AppProjectManager.getProject(projectId),
                    null
                );
            })
        );

        return projects.filter((value: ProjectInstance | null): boolean => {
            return value != null;
        }) as Array<ProjectInstance>;
    }

    public static async getProject(projectId: string | null): Promise<ProjectInstance> {
        if (projectId == null) {
            return new ProjectInstance(projectId);
        }

        const content: string = await AppFileApiHelper.readTextFile(
            AppProjectManager.getMetaFilePath(projectId)
        );

        return new ProjectInstance(projectId, content);
    }

    public static getProjectTextResource(project: ProjectInstance, path: string, fromAutoSave: boolean = false): Promise<string> {
        return AppFileApiHelper.readTextFile(
            AppProjectManager.getProjectRootPath(project.id, fromAutoSave) + path
        );
    }

    public static async getNewProjectId(): Promise<string> {
        return Promise.resolve<string>(UIHelper.getUUID());
    }

    public static async restoreCurrentSession(): Promise<void> {
        // project and scene are still in the device memory :)
        if (ProjectManagerStore.getCurrentProject() != null && GlobalHelper.eaiwsSession.session != null) {
            // check if session is still alive
            const alive: boolean = await GlobalHelper.eaiwsSession.session.keepAlive();
            if (!alive) {
                const autoSaveId: string | undefined = PersistentStore.getState().autoSaveId;
                await SceneActions.newScene(
                    {
                        projectLoadingHook: async (): Promise<void> => {
                            const couldRestoreSession: boolean = await AppProjectManager.restoreLastProject(autoSaveId);
                            if (!couldRestoreSession) {
                                await AppProjectManager.loadProject(null);
                            }
                        }
                    },
                    SaveType.NOT
                );
            }
        }
    }

    public static async restoreLastProject(projectId?: string): Promise<boolean> {
        if (!utils.string.isNullOrEmpty(projectId)) {
            const useAutoSave: boolean = await AppProjectManager.containsContainerDocument(
                AppProjectManager.getContainerPath(projectId, true)
            );
            const wasSavedBefore: boolean = await AppProjectManager.containsContainerDocument(
                AppProjectManager.getContainerPath(projectId, false)
            );

            if (useAutoSave === false && wasSavedBefore === false) {
                return false;
            }

            await AppProjectManager.loadProject(projectId, {
                loadFromAutoSave: useAutoSave,
                markAsNew: !wasSavedBefore
            });

            return true;
        }

        return false;
    }

    public static async loadProject(projectId: string | null, options?: ProjectLoadOptions): Promise<void> {
        const project: ProjectInstance = await this.getProject(projectId);
        const useTempPath: boolean = options != null && options.loadFromAutoSave === true;
        if (options != null && options.markAsNew === true) {
            project._wasNotSaved();
        }

        // Note: check `projectId` instead of `project.id` in order to avoid bugs if the behavior of the
        // `ProjectInstance` constructor changes
        if (projectId != null) {
            switch (project.loadMethod) {
                case ProjectLoadMethod.CONTAINER:
                    const container: core.io.DocumentContainer = new AppDocumentContainer(
                        AppProjectManager.getContainerPath(projectId, useTempPath)
                    );
                    await GlobalHelper.coreApp.document.loadFromContainer(container);
                    break;
                case ProjectLoadMethod.OBK:
                    // Note: does not support `tmp` projects -> auto save projects are stored as `container`
                    const content: ArrayBuffer = await AppFileApiHelper.readBinaryFile(
                        AppProjectManager.getProjectRootPath(project.id) + 'session.obk'
                    );
                    if (content != null) {
                        await SessionActions.loadObkFile(
                            new Blob([new Uint8Array(content)], {type: 'application/zip'})
                        );
                    }
                    break;
                default:
                    break;
            }
        }

        // project post processing
        await FloorPlanManager.loadFloorPlan(useTempPath, project);
        ProjectManagerActions.setLoadedProject(project);

        if (useTempPath === false && projectId != null) { // FixMe: remove old tmp folder even if no saved project is loaded
            UIHelper.ignoreReject(AppFileApiWrapper.remove(TempProjectRootFolder), false);
        }

        SceneActions.setUnsavedChanges(useTempPath);
    }

    public static async saveTempProject(project: ProjectInstance): Promise<void> {
        await AppProjectManager.saveToContainer(project.id, true);
    }

    public static async saveProject(project: ProjectInstance, saveAsCopy: boolean = false, options?: ProjectSaveOptions): Promise<ProjectInstance> {
        const mergeId: string = project.id;
        if (saveAsCopy === true) {
            project = await AppProjectManager.cloneProjectInstance(project, true);
        }

        if (options == null || options.metaDataOnly !== true) {
            if (project.loadMethod !== ProjectLoadMethod.CONTAINER) {
                project.loadMethod = ProjectLoadMethod.CONTAINER;
            }

            AppProjectManager.saveTempResources(mergeId, project.id);

            await AppProjectManager.saveToContainer(project.id, false);

            const onPhone: boolean = ViewSettingsStore.getState().layoutType === LayoutType.SMALL;
            project.image = await ScreenshotHelper.getProjectPreviewImage(onPhone);
        }

        await AppFileApiHelper.writeFile(
            AppProjectManager.getMetaFilePath(project.id),
            project.toJson(options == null || options.updateTime !== false)
        );

        project._wasSaved();

        return project;
    }

    public static async renameProject(projectId: string, name: string): Promise<void> {
        const project: ProjectInstance = await AppProjectManager.getProject(projectId);
        if (project != null) {
            project.name = name;
            await AppProjectManager.saveProject(project, false, {metaDataOnly: true, updateTime: false});
        }
    }

    public static async duplicateProject(projectId: string, newName?: string): Promise<void> {
        const project: ProjectInstance = await AppProjectManager.getProject(projectId);
        if (project != null) {
            const newProject: ProjectInstance = await AppProjectManager.cloneProjectInstance(project, true);
            if (!utils.string.isNullOrEmpty(newName)) {
                newProject.name = newName;
            }
            await AppProjectManager.saveProject(newProject, false, {metaDataOnly: true, updateTime: true});
        }
    }

    public static removeProject(projectId: string): Promise<void> {
        return AppFileApiWrapper.remove(AppProjectManager.getProjectRootPath(projectId));
    }

    public static async migrateProjects(): Promise<void> {
        let version: number = ProjectManagerApiVersion.PROJECT_API_BASED;
        const rawVersion: string = await AppFileApiHelper.readTextFile(ProjectApiVersionInformationPath);
        if (rawVersion != null) {
            version = parseInt(rawVersion, 10);
        }

        if (isNaN(version)) {
            // FixMe: log error
            return;
        }

        if (version === ProjectManagerApiVersion.PROJECT_API_BASED) {
            try {
                await AppProjectManager.migrateProjectFromOldProjectsApi();
                await AppProjectManager.updateProjectApiVersion(ProjectManagerApiVersion.FS_API_BASED);

                // migration done -> check if there are other migration steps
                return AppProjectManager.migrateProjects();
            } catch (error) {
                 Raven.captureException(error);
            }
        }
    }
    private static getProjectRootPath(projectId: string, tmpProject: boolean = false): string {
        return (tmpProject ? TempProjectRootFolder : ProjectRootFolder) + projectId + '/';
    }

    private static getMetaFilePath(projectId: string, tmpProject: boolean = false): string {
        return AppProjectManager.getProjectRootPath(projectId, tmpProject) + 'meta.json';
    }

    private static getContainerPath(projectId: string, tmpProject: boolean = false): string {
        return AppProjectManager.getProjectRootPath(projectId, tmpProject) + 'container/';
    }

    private static async cloneProjectInstance(project: ProjectInstance, copyFiles: boolean = false): Promise<ProjectInstance> {
        const newProject: ProjectInstance = new ProjectInstance(null, project.toJson(false));

        if (copyFiles === true && project.isNew === false) {
            await AppFileApiWrapper.copy(
                AppProjectManager.getProjectRootPath(project.id),
                AppProjectManager.getProjectRootPath(newProject.id),
                false
            );
        }

        return newProject;
    }

    private static async saveTempResources(tempId: string, projectId: string): Promise<void> {
        const tmpPath: string = AppProjectManager.getProjectRootPath(tempId, true);
        let files: Array<string>;

        try {
            files = await AppFileApiWrapper.list(tmpPath);
        } catch (_) {
            // no tmp path -> nothing to do
            return;
        }

        await Promise.all<void>(
            files.map((filename: string): Promise<void> => {
                return AppFileApiWrapper.copy(
                    tmpPath + filename,
                    AppProjectManager.getProjectRootPath(projectId, false) + filename,
                    true
                );
            })
        );

        await AppFileApiWrapper.remove(tmpPath);
    }

    private static async containsContainerDocument(path: string): Promise<boolean> {
        try {
            const files: Array<string> = await AppFileApiWrapper.list(path);
            return files.indexOf('document.json') !== -1;
        } catch (_) {
            return false;
        }
    }

    private static async saveToContainer(projectId: string, tmpProject: boolean = false): Promise<void> {
        const container: core.io.DocumentContainer = new AppDocumentContainer(
            AppProjectManager.getContainerPath(projectId, tmpProject)
        );

        return GlobalHelper.coreApp.document.saveToContainer(
            container,
            {
                embedGeometries: true,
                embedTextures: true
            }
        );
    }

    private static updateProjectApiVersion(version: number): Promise<void> {
        return AppFileApiHelper.writeFile(ProjectApiVersionInformationPath, version.toString());
    }

    private static async migrateProjectFromOldProjectsApi(): Promise<void> {
        const projects: Array<{id: number}> = await sendMessage<any, Array<{id: number}>>({
            type: 'project',
            action: 'list',
            arguments: {
                fields: ['id']
            }
        });

        // Note: do not convert multiple projects at the same time
        for (const entry of projects) {
            const oldProject: ApiVNullProjectData = await sendMessage<any, ApiVNullProjectData>({
                type: 'project',
                action: 'get',
                arguments: {
                    id: entry.id
                }
            });

            const properties: ProjectProperties = {
                name: oldProject.name,
                mtime: oldProject.mtime ? oldProject.mtime : Math.floor(Date.now() / 1000),
                crtime: oldProject.crtime ? oldProject.crtime : Math.floor(Date.now() / 1000),
                image: oldProject.image,
                attributes: oldProject.attributes != null ? JSON.parse(oldProject.attributes) as ProjectAttributes : undefined,
                loadMethod: ProjectLoadMethod.OBK
            };

            let project: ProjectInstance = new ProjectInstance(null, properties);
            project = await AppProjectManager.saveProject(project, false, {
                updateTime: false,
                metaDataOnly: true
            });
            if (!utils.string.isNullOrEmpty(oldProject.content)) {
                const content: Uint8Array | null = Converter.base64ToUint8Array(oldProject.content);
                if (content != null) {
                    await AppFileApiHelper.writeFile(
                        AppProjectManager.getProjectRootPath(project.id) + 'session.obk',
                        content.buffer as ArrayBuffer
                    );
                }
            }
        }
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/AppProjectManager.ts